import React, { useState, useEffect, useCallback } from 'react'
import { Box, Typography, IconButton } from '@mui/material'
import RestartAltIcon from '@mui/icons-material/RestartAlt'

// Emoji pairs for the memory game - calming nature theme
const CARD_EMOJIS = ['🌸', '🌺', '🌻', '🌼', '🍀', '🌿', '🦋', '🌈']

function shuffleArray(array) {
  const newArray = [...array]
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[newArray[i], newArray[j]] = [newArray[j], newArray[i]]
  }
  return newArray
}

function createCards() {
  const pairs = CARD_EMOJIS.flatMap((emoji, index) => [
    { id: index * 2, emoji, matched: false },
    { id: index * 2 + 1, emoji, matched: false },
  ])
  return shuffleArray(pairs)
}

export default function MemoryGame() {
  const [cards, setCards] = useState(() => createCards())
  const [flippedIndices, setFlippedIndices] = useState([])
  const [moves, setMoves] = useState(0)
  const [isChecking, setIsChecking] = useState(false)
  const [gameComplete, setGameComplete] = useState(false)

  const matchedCount = cards.filter(c => c.matched).length

  const resetGame = useCallback(() => {
    setCards(createCards())
    setFlippedIndices([])
    setMoves(0)
    setIsChecking(false)
    setGameComplete(false)
  }, [])

  // Check for match when two cards are flipped
  useEffect(() => {
    if (flippedIndices.length !== 2) return

    setIsChecking(true)
    const [first, second] = flippedIndices

    if (cards[first].emoji === cards[second].emoji) {
      // Match found!
      setTimeout(() => {
        setCards(prev => prev.map((card, idx) =>
          idx === first || idx === second
            ? { ...card, matched: true }
            : card
        ))
        setFlippedIndices([])
        setIsChecking(false)
      }, 500)
    } else {
      // No match - flip back
      setTimeout(() => {
        setFlippedIndices([])
        setIsChecking(false)
      }, 1000)
    }
  }, [flippedIndices, cards])

  // Check for game completion
  useEffect(() => {
    if (matchedCount === cards.length && cards.length > 0) {
      setGameComplete(true)
    }
  }, [matchedCount, cards.length])

  const handleCardClick = (index) => {
    // Ignore if already checking, card is matched, or already flipped
    if (isChecking) return
    if (cards[index].matched) return
    if (flippedIndices.includes(index)) return
    if (flippedIndices.length >= 2) return

    setFlippedIndices(prev => [...prev, index])
    if (flippedIndices.length === 1) {
      setMoves(prev => prev + 1)
    }
  }

  const isCardFlipped = (index) => {
    return flippedIndices.includes(index) || cards[index].matched
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        padding: 2,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: '14px', color: '#6b7280', fontWeight: 500 }}>
          Memory Match
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: '12px', color: '#9ca3af' }}>
            Moves: {moves}
          </Typography>
          <IconButton
            onClick={resetGame}
            size="small"
            sx={{ color: '#6b7280' }}
          >
            <RestartAltIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Game completion message */}
      {gameComplete && (
        <Box
          sx={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            borderRadius: '12px',
            padding: '12px 20px',
            textAlign: 'center',
          }}
        >
          <Typography sx={{ color: '#ffffff', fontWeight: 600, fontSize: '14px' }}>
            🎉 Great job! Completed in {moves} moves!
          </Typography>
        </Box>
      )}

      {/* Card grid - 4x4 */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '8px',
          maxWidth: '280px',
        }}
      >
        {cards.map((card, index) => (
          <Box
            key={card.id}
            onClick={() => handleCardClick(index)}
            sx={{
              width: '60px',
              height: '60px',
              borderRadius: '12px',
              cursor: card.matched ? 'default' : 'pointer',
              perspective: '1000px',
              '&:hover': {
                transform: card.matched ? 'none' : 'scale(1.05)',
              },
              transition: 'transform 0.2s ease',
            }}
          >
            <Box
              sx={{
                width: '100%',
                height: '100%',
                position: 'relative',
                transformStyle: 'preserve-3d',
                transition: 'transform 0.4s ease',
                transform: isCardFlipped(index) ? 'rotateY(180deg)' : 'rotateY(0)',
              }}
            >
              {/* Card back */}
              <Box
                sx={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  backfaceVisibility: 'hidden',
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(139, 92, 246, 0.3)',
                }}
              >
                <Typography sx={{ fontSize: '20px', color: '#ffffff', opacity: 0.5 }}>?</Typography>
              </Box>

              {/* Card front */}
              <Box
                sx={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  backfaceVisibility: 'hidden',
                  transform: 'rotateY(180deg)',
                  background: card.matched 
                    ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                    : '#ffffff',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: card.matched ? 'none' : '2px solid #e5e7eb',
                  boxShadow: card.matched 
                    ? '0 2px 8px rgba(16, 185, 129, 0.3)'
                    : '0 2px 4px rgba(0, 0, 0, 0.1)',
                }}
              >
                <Typography sx={{ fontSize: '28px' }}>{card.emoji}</Typography>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Progress */}
      <Typography sx={{ fontSize: '12px', color: '#9ca3af' }}>
        {matchedCount / 2} of {cards.length / 2} pairs found
      </Typography>
    </Box>
  )
}
